import { useState, useEffect } from 'react';
import { useProviderAuth, ProviderAuthProvider } from '../contexts/ProviderAuthContext';
import { ProviderAuthForm } from '../components/provider/ProviderAuthForm';
import { ProfileEditor } from '../components/provider/ProfileEditor';
import { MediaUploader } from '../components/provider/MediaUploader';
import { DocumentUploader } from '../components/provider/DocumentUploader';
import { EmailVerificationBanner, EmailVerificationStatus } from '../components/provider/EmailVerification';
import { ProviderOnboarding } from '../components/provider/ProviderOnboarding';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Building2, Image, LogOut, User, Loader2, FileCheck, MessageCircle, FileText, Sparkles, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ProviderDashboardContent() {
  const { user, token, loading, logout, isAuthenticated } = useProviderAuth();
  
  // Profile state
  const [hasProfile, setHasProfile] = useState(null); // null = loading, true/false = known
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);
  
  // Check if user has a profile
  useEffect(() => {
    if (isAuthenticated && token) {
      checkProfile();
    }
  }, [isAuthenticated, token]);
  
  const checkProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/api/provider/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const profile = await res.json();
        // Has profile if business_name is not default
        setHasProfile(profile.business_name && profile.business_name !== 'Da completare');
      } else if (res.status === 404) {
        setHasProfile(false);
      } else {
        setHasProfile(false);
      }
    } catch (err) {
      console.error('Profile check error:', err);
      setHasProfile(false);
    } finally {
      setCheckingProfile(false);
    }
  };
  
  const handleOnboardingComplete = (result) => {
    // Profile saved, refresh state
    setHasProfile(true);
    setShowOnboarding(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-umbria-paper to-umbria-stone/30">
        <Loader2 className="h-8 w-8 animate-spin text-umbria-olive" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <ProviderAuthForm />;
  }
  
  // Checking profile status
  if (checkingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-umbria-paper to-umbria-stone/30">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-umbria-olive mx-auto mb-4" />
          <p className="text-umbria-muted">Caricamento profilo...</p>
        </div>
      </div>
    );
  }
  
  // Show onboarding flow
  if (showOnboarding) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-umbria-paper to-umbria-stone/20 p-4 sm:p-8">
        <div className="max-w-2xl mx-auto">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-umbria-olive/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-umbria-olive" />
              </div>
              <div>
                <h1 className="font-serif text-xl font-semibold text-umbria-charcoal">Feel Umbria</h1>
                <p className="text-xs text-umbria-muted">Onboarding Guidato</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={logout}
              className="text-umbria-muted"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
          
          <ProviderOnboarding 
            onComplete={handleOnboardingComplete}
            onSkip={() => {
              setShowOnboarding(false);
              setHasProfile(true); // Go to form editor
            }}
          />
        </div>
      </div>
    );
  }
  
  // Show onboarding choice if no profile yet
  if (hasProfile === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-umbria-paper to-umbria-stone/20 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-umbria-olive/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-umbria-olive" />
            </div>
            <h1 className="text-3xl font-serif font-semibold text-umbria-charcoal mb-2">
              Benvenuto su Feel Umbria
            </h1>
            <p className="text-umbria-muted">
              Crea la scheda della tua attività per farti scoprire dai viaggiatori
            </p>
          </div>
          
          {/* Choice cards */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Conversational option */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-umbria-olive group"
              onClick={() => setShowOnboarding(true)}
              data-testid="onboarding-chat-option"
            >
              <CardHeader className="pb-2">
                <div className="w-12 h-12 bg-umbria-olive/10 rounded-xl flex items-center justify-center mb-3 group-hover:bg-umbria-olive/20 transition-colors">
                  <MessageCircle className="w-6 h-6 text-umbria-olive" />
                </div>
                <CardTitle className="text-lg">Compilazione Guidata</CardTitle>
                <CardDescription>
                  Raccontami della tua attività e compilo io la scheda
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-umbria-olive rounded-full" />
                    Conversazione naturale
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-umbria-olive rounded-full" />
                    Nessun form da compilare
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-umbria-olive rounded-full" />
                    Rivedi prima di salvare
                  </li>
                </ul>
                <div className="flex items-center text-umbria-olive font-medium text-sm">
                  Inizia la chat
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </CardContent>
            </Card>
            
            {/* Classic form option */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-gray-400 group"
              onClick={() => setHasProfile(true)}
              data-testid="onboarding-form-option"
            >
              <CardHeader className="pb-2">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-3 group-hover:bg-gray-200 transition-colors">
                  <FileText className="w-6 h-6 text-gray-600" />
                </div>
                <CardTitle className="text-lg">Compilazione Classica</CardTitle>
                <CardDescription>
                  Compila tu stesso i campi del profilo
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                    Form strutturato
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                    Controllo totale
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                    Per chi preferisce i form
                  </li>
                </ul>
                <div className="flex items-center text-gray-600 font-medium text-sm">
                  Vai al form
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Logout */}
          <div className="text-center mt-8">
            <button 
              onClick={logout}
              className="text-sm text-umbria-muted hover:text-umbria-charcoal"
            >
              Esci ({user?.email})
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal dashboard with profile
  return (
    <div className="min-h-screen bg-gradient-to-br from-umbria-paper to-umbria-stone/20">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-umbria-olive/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-umbria-olive" />
            </div>
            <div>
              <h1 className="font-serif text-xl font-semibold text-umbria-charcoal">
                Feel Umbria
              </h1>
              <p className="text-xs text-umbria-muted">Portale Fornitori</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <EmailVerificationStatus compact />
            <div className="flex items-center gap-2 text-sm text-umbria-charcoal">
              <User className="h-4 w-4 text-umbria-muted" />
              <span className="hidden sm:inline">{user?.email}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={logout}
              className="text-umbria-muted hover:text-umbria-charcoal"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Email Verification Banner */}
        <EmailVerificationBanner />

        {/* Welcome */}
        <div className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-umbria-charcoal">
            Benvenuto, {user?.business_name || 'Partner'}
          </h2>
          <p className="text-umbria-muted mt-1">
            Gestisci il tuo profilo, documenti e immagini su Feel Umbria
          </p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-white/50 border">
            <TabsTrigger value="profile" className="gap-2" data-testid="tab-profile">
              <Building2 className="h-4 w-4" />
              Profilo
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-2" data-testid="tab-documents">
              <FileCheck className="h-4 w-4" />
              Documenti
            </TabsTrigger>
            <TabsTrigger value="media" className="gap-2" data-testid="tab-media">
              <Image className="h-4 w-4" />
              Immagini
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <ProfileEditor />
          </TabsContent>

          <TabsContent value="documents">
            <DocumentUploader />
          </TabsContent>

          <TabsContent value="media">
            <MediaUploader />
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white/50 mt-16">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-umbria-muted">
            <p>Feel Umbria - Portale Fornitori</p>
            <p>
              Hai bisogno di aiuto? Contattaci a{' '}
              <a href="mailto:partner@feelumbria.com" className="text-umbria-olive hover:underline">
                partner@feelumbria.com
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function ProviderDashboard() {
  return (
    <ProviderAuthProvider>
      <ProviderDashboardContent />
    </ProviderAuthProvider>
  );
}
